import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * ShopVisibilityStatus Component
 *
 * Displays the visibility status of a shop location with detailed requirements.
 * Shows owners what's missing to make their location publicly visible.
 *
 * Props:
 * - shop: Shop location object with visibilityStatus field
 * - compact: boolean - show compact version (default: false)
 */
const ShopVisibilityStatus = ({ shop, compact = false }) => {
  const navigate = useNavigate();
  if (!shop) return null;

  const visibilityStatus = shop.visibilityStatus || {};
  const isVisible = shop.isPubliclyVisible === true;
  const failedChecks = visibilityStatus.failedChecks || [];

  // Requirements checklist
  const requirements = [
    {
      id: "approved",
      label: "Admin approved",
      met: visibilityStatus.approved === true,
      action: null, // Can't control this
    },
    {
      id: "address",
      label: "Complete address",
      met: visibilityStatus.hasCompleteAddress === true,
      action: `/dashboard/locations/${shop._id}/edit`,
    },
    {
      id: "content",
      label: "Photos or description",
      met: visibilityStatus.hasContent === true,
      action: `/dashboard/shop/${shop._id}`,
    },
    {
      id: "hours",
      label: "Business hours set",
      met: visibilityStatus.hasBusinessHours === true,
      action: `/dashboard/shop/${shop._id}/hours`,
    },
    {
      id: "bookings",
      label: "Accepting bookings",
      met: visibilityStatus.acceptingBookings === true,
      action: `/dashboard/shop/${shop._id}/settings`,
    },
    {
      id: "professionals",
      label: "Has professionals",
      met: visibilityStatus.hasActiveProfessionals === true,
      action: `/dashboard/professionals`,
    },
    {
      id: "photos",
      label: "At least 1 pro has photos",
      met: visibilityStatus.professionalsHavePhotos === true,
      action: `/dashboard/professionals`,
    },
    {
      id: "services",
      label: "At least 1 pro has services",
      met: visibilityStatus.professionalsHaveServices === true,
      action: `/dashboard/services`,
    },
    {
      id: "schedules",
      label: "At least 1 pro has schedules",
      met: visibilityStatus.professionalsHaveSchedules === true,
      action: `/dashboard/professionals`,
    },
  ];

  const metCount = requirements.filter((r) => r.met).length;
  const totalCount = requirements.length;
  const progress = Math.round((metCount / totalCount) * 100);

  // Hide completely if 100% complete
  if (progress === 100) {
    return null;
  }

  // Compact version - status message for listing page
  if (compact) {
    const shopPath = shop.slug
      ? `/dashboard/shop/s/${shop.slug}`
      : `/dashboard/shop/${shop._id}`;

    if (isVisible) {
      return (
        <div
          style={{
            marginTop: "12px",
            padding: "10px 12px",
            borderRadius: "8px",
            background: "#ecfdf5",
            border: "1px solid #86efac",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span style={{ fontSize: "16px" }}>✅</span>
          <div style={{ flex: 1 }}>
            <div
              style={{ fontSize: "13px", fontWeight: "600", color: "#059669" }}
            >
              Publicly Visible
            </div>
            <div style={{ fontSize: "11px", color: "#047857" }}>
              This location appears in customer searches
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={() => navigate(shopPath)}
        style={{
          marginTop: "12px",
          padding: "10px 12px",
          borderRadius: "8px",
          background: "#fef3c7",
          border: "1px solid #fbbf24",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          transition: "all 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#fde68a";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#fef3c7";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        title="Click to view requirements"
      >
        <span style={{ fontSize: "16px" }}>⚠️</span>
        <div style={{ flex: 1 }}>
          <div
            style={{ fontSize: "13px", fontWeight: "600", color: "#92400e" }}
          >
            Not Visible Yet
          </div>
          <div style={{ fontSize: "11px", color: "#78350f" }}>
            This location is not appearing in customer searches
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <div
            style={{
              padding: "4px 8px",
              borderRadius: "6px",
              background: "#fff",
              fontSize: "11px",
              fontWeight: "600",
              color: "#92400e",
            }}
          >
            {progress}%
          </div>
          <span style={{ fontSize: "14px", color: "#78350f" }}>→</span>
        </div>
      </div>
    );
  }

  // Full version - detailed status
  return (
    <div
      style={{
        background: isVisible ? "#ecfdf5" : "#fef3c7",
        border: `2px solid ${isVisible ? "#86efac" : "#fbbf24"}`,
        borderRadius: "12px",
        padding: "16px",
        marginTop: "12px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "20px" }}>{isVisible ? "✅" : "⚠️"}</span>
          <div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "700",
                color: isVisible ? "#059669" : "#92400e",
              }}
            >
              {isVisible ? "Publicly Visible" : "Not Visible Yet"}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: isVisible ? "#047857" : "#78350f",
              }}
            >
              {isVisible
                ? "This location appears in customer searches"
                : `${metCount} of ${totalCount} requirements met`}
            </div>
          </div>
        </div>
        {!isVisible && (
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              fontWeight: "700",
              color: "#92400e",
            }}
          >
            {progress}%
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {!isVisible && (
        <div
          style={{
            width: "100%",
            height: "6px",
            background: "#fde68a",
            borderRadius: "3px",
            marginBottom: "16px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: "#f59e0b",
              transition: "width 0.3s ease",
              borderRadius: "3px",
            }}
          />
        </div>
      )}

      {/* Requirements List */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div
          style={{
            fontSize: "12px",
            fontWeight: "600",
            color: "#78350f",
            marginBottom: "4px",
          }}
        >
          Setup Requirements:
        </div>
        {requirements.map((req) => (
          <div
            key={req.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "13px",
              color: req.met ? "#047857" : "#78350f",
              opacity: req.met ? 0.7 : 1,
            }}
          >
            <div
              style={{
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                background: req.met ? "#86efac" : "#fde68a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                flexShrink: 0,
              }}
            >
              {req.met ? "✓" : "○"}
            </div>
            <span
              style={{
                flex: 1,
                textDecoration: req.met ? "line-through" : "none",
              }}
            >
              {req.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ShopVisibilityStatus;
